import React, { useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import Text from "../../../../utils/Custom/text";
import CommonForm from "../../../../utils/commonComponent/commonForm";
import { CancelButton, SaveAndUpdate } from "../../../../utils/Custom/Button";
import { fetchQuery } from "../../../../utils/commonComponent/fetched";
import axiosWithToken from "../../../../utils/commonComponent/axiosTokenConfig";
import { BASE_URL } from "../../../../global";
import { getPermissions } from "../../../Pages/rbac/hasAccess";
import { validateRequiredFields } from "../../../../utils/commonComponent/errorAlert";

const scoreOptions = [
  { id: 1, name: "1" },
  { id: 2, name: "2" },
  { id: 3, name: "3" },
  { id: 4, name: "4" },
  { id: 5, name: "5" },
  { id: 6, name: "6" },
  { id: 7, name: "7" },
  { id: 8, name: "8" },
  { id: 9, name: "9" },
  { id: 10, name: "10" },
];

const EditPipeline = (props) => {
  const params = useParams();
  const permissions = getPermissions(props.moduleId);
  const navigate = useNavigate();

  const [inputStates, setInputStates] = React.useState({
    status: 1,
    interviewDate: new Date().toISOString().split("T")[0],
    nextActionDate: new Date().toISOString().split("T")[0],
  });

  const [candidates, setCandidates] = React.useState([]);
  const [candidatesRaw, setCandidatesRaw] = React.useState([]);
  const [requirements, setRequirements] = React.useState([]);
  const [requirementsRaw, setRequirementsRaw] = React.useState([]);

  const [stageOptions, setStageOptions] = React.useState([]);
  const [stageStatusOptions, setStageStatusOptions] = React.useState([]);
  const [modeOptions, setModeOptions] = React.useState([]);
  const [rejectionReasonOptions, setRejectionReasonOptions] = React.useState([]);

  const isRejectedStatus = (val = inputStates.stageStatus) => {
    if (!val) return false;
    const selectedObj = stageStatusOptions.find((m) => m.id == val);
    const name = selectedObj ? String(selectedObj.name).toLowerCase() : String(val).toLowerCase();
    return name.includes("reject") || String(val) === "4";
  };

  const updateInputObject = (key, value) => {
    setInputStates((prev) => {
      const updated = { ...prev, [key]: value };
      if (key === "stageStatus" && !isRejectedStatus(value)) {
        updated.rejectionReason = "";
        updated.rejectionDetails = "";
      }
      return updated;
    });
  };

  const fetchNextPipelineId = async () => {
    try {
      const [last] = await fetchQuery(
        `SELECT pipelineId FROM talentAcqPipeline ORDER BY id DESC LIMIT 1`
      );
      const lastId = parseInt(
        last?.pipelineId?.match(/\d+$/)?.[0] || "0",
        10
      );
      const nextId = `PL-${String(lastId + 1).padStart(3, "0")}`;
      updateInputObject("pipelineId", nextId);
    } catch (error) {
      console.error("Error fetching last Pipeline ID:", error);
      updateInputObject("pipelineId", "PL-001");
    }
  };

  const fetchDropdownOptions = async () => {
    try {
      const [candidateList, requirementList, masterList] = await Promise.all([
        fetchQuery("SELECT id, CandId, candidateName FROM talentAcqCandidate WHERE status = 1"),
        fetchQuery("SELECT id, requestedId, positionTitle, Client FROM talentAcqRequirement WHERE status = 1"),
        fetchQuery("SELECT id, type, Value FROM talentAcquisitionMaster WHERE status = 1"),
      ]);

      setCandidatesRaw(candidateList || []);
      setCandidates((candidateList || []).map((c) => ({
        id: c.id,
        name: c.CandId,
      })));

      setRequirementsRaw(requirementList || []);
      setRequirements((requirementList || []).map((r) => ({ id: r.id, name: `${r.requestedId} - ${r.positionTitle}` })));

      if (masterList && masterList.length > 0) {
        setStageOptions(masterList.filter((m) => m.type == 5).map((m) => ({ id: m.id, name: m.Value })));
        setStageStatusOptions(masterList.filter((m) => m.type == 6).map((m) => ({ id: m.id, name: m.Value })));
        setModeOptions(masterList.filter((m) => m.type == 7).map((m) => ({ id: m.id, name: m.Value })));
        setRejectionReasonOptions(masterList.filter((m) => m.type == 9).map((m) => ({ id: m.id, name: m.Value })));
      }
    } catch (error) {
      console.error("Error fetching dropdown options:", error);
    }
  };

  const fetchDetails = async () => {
    try {
      const [record] = await fetchQuery(
        `SELECT p.*, c.candidateName 
         FROM talentAcqPipeline p 
         LEFT JOIN talentAcqCandidate c ON p.candId = c.id 
         WHERE p.id = ${params.id}`
      );
      if (record) {
        if (record.interviewDate) record.interviewDate = record.interviewDate.split("T")[0];
        if (record.nextActionDate) record.nextActionDate = record.nextActionDate.split("T")[0];
        setInputStates(record);
      }
    } catch (error) {
      console.error("Error fetching pipeline data:", error);
    }
  };

  // Calculate Average
  useEffect(() => {
    const t = parseFloat(inputStates.technicalScore) || 0;
    const k = parseFloat(inputStates.Knowledge) || 0;
    const c = parseFloat(inputStates.communication) || 0;
    const activeScores = [inputStates.technicalScore, inputStates.Knowledge, inputStates.communication].filter(
      (v) => v !== undefined && v !== null && v !== ""
    );
    if (activeScores.length > 0) {
      const avg = (t + k + c) / activeScores.length;
      updateInputObject("overallScore", avg.toFixed(2));
    }
  }, [inputStates.technicalScore, inputStates.Knowledge, inputStates.communication]);

  useEffect(() => {
    if (inputStates.requirementId) {
      const reqObj = requirementsRaw.find((r) => r.id == inputStates.requirementId);
      if (reqObj) {
        if (inputStates.position !== reqObj.positionTitle || inputStates.client !== reqObj.Client) {
          setInputStates((prev) => ({
            ...prev,
            position: reqObj.positionTitle || "",
            client: reqObj.Client || "",
          }));
        }
      }
    }
  }, [inputStates.requirementId, requirementsRaw]);

  useEffect(() => {
    if (inputStates.candId) {
      const candObj = candidatesRaw.find((c) => c.id == inputStates.candId);
      if (candObj) {
        if (inputStates.candidateName !== candObj.candidateName) {
          setInputStates((prev) => ({
            ...prev,
            candidateName: candObj.candidateName || "",
          }));
        }
      }
    }
  }, [inputStates.candId, candidatesRaw]);

  const onSubmit = async () => {
    const fieldsToValidate = [
      { value: inputStates.pipelineId, label: "Pipeline ID is required" },
      { value: inputStates.requirementId, label: "Requirement is required" },
      { value: inputStates.candId, label: "Candidate is required" },
      { value: inputStates.stage, label: "Stage is required" },
      { value: inputStates.stageStatus, label: "Stage Status is required" },
    ];

    if (!validateRequiredFields(fieldsToValidate)) return;

    const payload = { ...inputStates };
    if (!isRejectedStatus(payload.stageStatus)) {
      payload.rejectionReason = "";
      payload.rejectionDetails = "";
    }

    try {
      const url = BASE_URL + `talentAcqPipeline`;
      params.id != 0
        ? await axiosWithToken.patch(`${url}/${params.id}`, payload)
        : await axiosWithToken.post(url, payload);

      toast.success(
        params.id != 0 ? "Pipeline Updated Successfully!" : "Pipeline Created Successfully!"
      );
      if (params.id == 0) {
        setTimeout(() => navigate("/talent-acquisition/pipeline"), 1000);
      } else {
        setTimeout(() => window.location.reload(), 1000);
      }
    } catch (error) {
      console.error("Submission failed:", error);
    }
  };

  useEffect(() => {
    fetchDropdownOptions();
    if (params.id == 0) fetchNextPipelineId();
  }, []);

  useEffect(() => {
    if (params.id != 0) fetchDetails();
  }, [params]);

  const inputFields = [
    {
      type: "TextField",
      label: "Pipeline ID *",
      stateKey: "pipelineId",
      disabled: true,
    },
    {
      type: "Autocomplete",
      label: "Requirement *",
      stateKey: "requirementId",
      options: requirements,
      valueKey: "id",
      getOptionLabelKey: "name",
    },
    {
      type: "Autocomplete",
      label: "Candidate *",
      stateKey: "candId",
      options: candidates,
      valueKey: "id",
      getOptionLabelKey: "name",
    },
    {
      type: "TextField",
      label: "Candidate Name",
      stateKey: "candidateName",
      disabled: true,
    },
    {
      type: "TextField",
      label: "Position",
      stateKey: "position",
      disabled: true,
    },
    {
      type: "TextField",
      label: "Client",
      stateKey: "client",
      disabled: true,
    },
    {
      type: "Autocomplete",
      label: "Stage *",
      stateKey: "stage",
      options: stageOptions,
      valueKey: "id",
      getOptionLabelKey: "name",
    },
    {
      type: "TextField",
      label: "Round No.",
      stateKey: "round",
      inputType: "number",
    },
    {
      type: "Autocomplete",
      label: "Technical Score",
      stateKey: "technicalScore",
      options: scoreOptions,
      valueKey: "id",
      getOptionLabelKey: "name",
    },
    {
      type: "Autocomplete",
      label: "ASME/Code Knowledge",
      stateKey: "Knowledge",
      options: scoreOptions,
      valueKey: "id",
      getOptionLabelKey: "name",
    },
    {
      type: "Autocomplete",
      label: "Communication",
      stateKey: "communication",
      options: scoreOptions,
      valueKey: "id",
      getOptionLabelKey: "name",
    },
    {
      type: "TextField",
      label: "Overall Score",
      stateKey: "overallScore",
      disabled: true,
    },
    {
      type: "Autocomplete",
      label: "Interviewer",
      stateKey: "interviewer",
      options: "employees",
      valueKey: "name",
      getOptionLabelKey: "name",
    },
    {
      type: "DatePicker",
      label: "Interview Date",
      stateKey: "interviewDate",
    },
    {
      type: "Autocomplete",
      label: "Mode",
      stateKey: "mode",
      options: modeOptions,
      valueKey: "id",
      getOptionLabelKey: "name",
    },
    {
      type: "Autocomplete",
      label: "Stage Status *",
      stateKey: "stageStatus",
      options: stageStatusOptions,
      valueKey: "id",
      getOptionLabelKey: "name",
    },
    rejectionReasonOptions.length > 0 ? {
      type: "Autocomplete",
      label: "Rejection Reason",
      stateKey: "rejectionReason",
      options: rejectionReasonOptions,
      valueKey: "name",
      getOptionLabelKey: "name",
    } : {
      type: "TextField",
      label: "Rejection Reason",
      stateKey: "rejectionReason",
    },
    {
      type: "TextField",
      label: "Rejection Detail",
      stateKey: "rejectionDetails",
    },
    {
      type: "TextField",
      label: "Days at Stage",
      stateKey: "daysAtStage",
      inputType: "number",
    },
    {
      type: "DatePicker",
      label: "Next Action Date",
      stateKey: "nextActionDate",
    },
  ];

  const visibleInputFields = inputFields.filter(
    (field) =>
      !["rejectionReason", "rejectionDetails"].includes(field.stateKey) ||
      isRejectedStatus()
  );

  return (
    <div>
      <Text variant="h3" bold className="text-blue-600">
        {params.id == 0 ? "Create Pipeline" : "Update Pipeline"}
      </Text>
      <br />
      <CommonForm
        inputStates={inputStates}
        updateInputObject={updateInputObject}
        inputFields={visibleInputFields}
      />
      <br />
      <div className="flex gap-10 mt-5 justify-end">
        <CancelButton
          id="btn-cancel"
          label="Cancel"
          outline
          onClick={() => {
            if (window.confirm("Are you sure you want to cancel?")) {
              window.history.back();
            }
          }}
        />
        {(permissions.save || permissions.allAccess) && (
          <SaveAndUpdate
            id="btn-save"
            label={params.id == 0 ? "Save" : "Update"}
            onClick={onSubmit}
          />
        )}
      </div>
    </div>
  );
};

export default EditPipeline;
